type ResolveMethod<T> = (...values: T[]) => void;
type RejectMethod<Error> = (reason: Error) => void;
type CallbackMethod<T> = (
  resolve: ResolveMethod<T>,
  reject: RejectMethod<Error>,
) => void;

export const debugError = (...msg: string[]) =>
  // eslint-disable-next-line no-console
  console.error('[FreedomApp]:', ...msg);

export const debug = (...args: Array<unknown>) => {
  if (import.meta.env.VITE_RUNTIME_ENV === 'development') {
    // eslint-disable-next-line no-console
    window.console.info('[Native communication]', ...args);
  }
};

/**
 * Freedom avia mini app and native apps communicate through API:
 * window.FreedomApp, window.FreedomWebApp via callbacks.
 * This method converts callbacks to Promises for more convenient development experience
 */
export const connect = <T>(callback: CallbackMethod<T>) => {
  return new Promise<T>((resolve, reject) => {
    try {
      callback(resolve, reject);
    } catch (e: unknown) {
      if (import.meta.env.VITE_RUNTIME_ENV !== 'production') {
        debugError(
          'If you are in development mode then read in README or in WIKI - https://wiki.yandex.ru/homepage/chocotravel/frontend/choco-mini-apps/freedom-mini-apps/authorization/',
        );
      }

      reject(e);
    }
  });
};

/**
 * Freedom avia mini app might be used in any environments.
 * Some of them might not provide native methods API, so we need to catch errors
 */
export const tryNativeMethod = ({
  callback,
  errorMessage,
  errorHandler,
}: {
  callback: () => void;
  errorMessage?: string;
  errorHandler?: (error: Error) => void;
}) => {
  try {
    callback();
  } catch (e: unknown) {
    // Throw an error if the caught error is not a `TypeError` to simplify debugging
    if (!(e instanceof TypeError)) {
      throw e;
    }

    const error = errorMessage ? new TypeError(errorMessage) : e;

    if (errorHandler) {
      errorHandler(error);
    } else if (window.FreedomWebApp.onNativeEventError) {
      window.FreedomWebApp.onNativeEventError(error);
    } else {
      throw error;
    }
  }
};
